import React, { useState, useMemo } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

// @material-ui/icons
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import styles from "assets/jss/material-dashboard-pro-react/components/tableStyle";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

const groupedTableStyles = (theme) => ({
  ...styles(theme),
  // Sticky group header (top level)
  stickyGroupHeader: {
    position: "sticky",
    top: 0,
    zIndex: 12, // Higher than column header
    backgroundColor: "white",
  },
  // Sticky column header (below group header)
  stickyColumnHeader: {
    position: "sticky",
    top: "36px", // Position below the group header (36px height)
    zIndex: 11, // Lower than group header but above content
    backgroundColor: "white",
  },
  // Simplified group header
  groupHeaderCell: {
    backgroundColor: grayColor[10],
    textAlign: "left",
    fontWeight: "600",
    fontSize: "0.9em",
    padding: "8px 16px",
    height: "36px",
    borderBottom: `1px solid ${grayColor[5]}`,
    whiteSpace: "nowrap", // Prevent wrapping
  },
  // Simplified column header
  columnHeaderCell: {
    backgroundColor: "white",
    fontWeight: "500",
    padding: "8px 16px",
    borderBottom: `2px solid ${grayColor[5]}`,
  },
  // Simple collapse button
  collapseButton: {
    marginLeft: "8px",
    padding: "4px",
    verticalAlign: "middle", // Keep button aligned with text
    "& .MuiSvgIcon-root": {
      fontSize: "1.2rem",
    },
  },
  // Simplified table container
  tableResponsive: {
    ...styles(theme).tableResponsive,
    overflowX: "auto",
  },
  // Collapsed cell styling
  collapsedCell: {
    backgroundColor: grayColor[11],
    textAlign: "center",
    fontStyle: "italic",
    color: grayColor[1],
  },
});

const useStyles = makeStyles(groupedTableStyles);

export default function GroupedTable(props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    tableHead,
    tableData,
    columnGroups,
    hover,
    colorsColls,
    coloredColls,
    customCellClasses,
    customClassesForCells,
    striped,
  } = props;

  // State to track which groups are collapsed
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // Process columnGroups to add collapse state and calculate colspan
  const processedGroups = useMemo(() => {
    if (!columnGroups || !columnGroups.groups) return null;

    const { groups, group_order } = columnGroups;
    const processedData = [];

    group_order.forEach((groupName) => {
      const group = groups[groupName];
      if (group && group.columns.length > 0) {
        processedData.push({
          name: groupName,
          displayName: group.name,
          columns: group.columns,
          columnIndices: group.column_indices,
          collapsed: collapsedGroups.has(groupName),
        });
      }
    });

    return processedData;
  }, [columnGroups, collapsedGroups]);

  // Toggle group collapse state
  const toggleGroupCollapse = (groupName) => {
    const newCollapsedGroups = new Set(collapsedGroups);
    if (newCollapsedGroups.has(groupName)) {
      newCollapsedGroups.delete(groupName);
    } else {
      newCollapsedGroups.add(groupName);
    }
    setCollapsedGroups(newCollapsedGroups);
  };

  // Calculate visible columns and their mappings
  const visibleColumnMapping = useMemo(() => {
    if (!processedGroups)
      return { visibleColumns: tableHead || [], mapping: [] };

    const visibleColumns = [];
    const mapping = [];

    processedGroups.forEach((group, groupIndex) => {
      if (group.collapsed) {
        // Add a single collapsed column placeholder with no text (group name is already in group header)
        visibleColumns.push("");
        mapping.push({
          type: "collapsed",
          groupName: group.name,
          originalIndices: group.columnIndices,
          isFirstInGroup: true,
          isLastInGroup: true,
          groupIndex: groupIndex,
        });
      } else {
        // Add all columns in the group
        group.columns.forEach((column, index) => {
          visibleColumns.push(column);
          mapping.push({
            type: "normal",
            groupName: group.name,
            originalIndex: group.columnIndices[index],
            isFirstInGroup: index === 0,
            isLastInGroup: index === group.columns.length - 1,
            groupIndex: groupIndex,
          });
        });
      }
    });

    return { visibleColumns, mapping };
  }, [processedGroups, tableHead]);

  // Create group header row data
  const createGroupHeaderRow = () => {
    if (!processedGroups) return null;

    const groupHeaders = [];
    processedGroups.forEach((group, index) => {
      const colspan = group.collapsed ? 1 : group.columns.length;
      groupHeaders.push({
        name: group.name,
        displayName: group.displayName,
        colspan: colspan,
        collapsed: group.collapsed,
        isFirst: index === 0,
        isLast: index === processedGroups.length - 1,
      });
    });

    return groupHeaders;
  };

  // Transform table data based on visible columns
  const transformedTableData = useMemo(() => {
    if (!tableData || !visibleColumnMapping.mapping.length) return tableData;

    return tableData.map((row) => {
      if (row.total || row.purchase) return row; // Keep special rows as-is

      const newRow = [];
      visibleColumnMapping.mapping.forEach((mapping) => {
        if (mapping.type === "collapsed") {
          newRow.push("...");
        } else {
          newRow.push(row[mapping.originalIndex] || "");
        }
      });
      return newRow;
    });
  }, [tableData, visibleColumnMapping.mapping]);

  const groupHeaderData = createGroupHeaderRow();

  return (
    <Table className={`${classes.table} ${classes.tableResponsive}`}>
      {tableHead !== undefined ? (
        <TableHead>
          {/* Sticky Group Header Row */}
          {processedGroups && (
            <TableRow className={classes.stickyGroupHeader}>
              {groupHeaderData.map((group, index) => (
                <TableCell
                  key={`group-${index}`}
                  className={classes.groupHeaderCell}
                  colSpan={group.colspan}
                  style={{
                    cursor: "pointer",
                    backgroundColor: group.collapsed
                      ? grayColor[8]
                      : grayColor[10],
                  }}
                  onClick={() => toggleGroupCollapse(group.name)}
                >
                  {group.displayName}
                  <Tooltip
                    title={
                      group.collapsed
                        ? t("rgm.cockpit.overview.table.expand_group")
                        : t("rgm.cockpit.overview.table.collapse_group")
                    }
                    arrow
                    enterDelay={500}
                  >
                    <IconButton
                      className={classes.collapseButton}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroupCollapse(group.name);
                      }}
                    >
                      {group.collapsed ? (
                        <ExpandMoreIcon />
                      ) : (
                        <ChevronRightIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          )}

          {/* Sticky Column Header Row */}
          <TableRow className={classes.stickyColumnHeader}>
            {visibleColumnMapping.visibleColumns.map((header, key) => {
              const mapping = visibleColumnMapping.mapping[key];
              const isCollapsedColumn = mapping && mapping.type === "collapsed";

              return (
                <TableCell
                  key={key}
                  className={`${classes.columnHeaderCell} ${isCollapsedColumn ? classes.collapsedCell : ""}`}
                >
                  {header}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
      ) : null}{" "}
      <TableBody>
        {transformedTableData.map((prop, key) => {
          let rowColor = "";
          let rowColored = false;
          if (prop.color !== undefined) {
            rowColor = prop.color;
            rowColored = true;
            prop = prop.data;
          }
          const tableRowClasses = cx({
            [classes.tableRowBody]: true,
            [classes.tableRowHover]: hover,
            [classes[rowColor + "Row"]]: rowColored,
            [classes.tableStripedRow]: striped && key % 2 === 0,
          });

          if (prop.total) {
            return (
              <TableRow key={key} hover={hover} className={tableRowClasses}>
                <TableCell
                  className={classes.tableCell}
                  colSpan={prop.colspan}
                />
                <TableCell
                  className={classes.tableCell + " " + classes.tableCellTotal}
                >
                  Total
                </TableCell>
                <TableCell
                  className={classes.tableCell + " " + classes.tableCellAmount}
                >
                  {prop.amount}
                </TableCell>
                {visibleColumnMapping.visibleColumns.length -
                  (prop.colspan - 0 + 2) >
                0 ? (
                  <TableCell
                    className={classes.tableCell}
                    colSpan={
                      visibleColumnMapping.visibleColumns.length -
                      (prop.colspan - 0 + 2)
                    }
                  />
                ) : null}
              </TableRow>
            );
          }

          if (prop.purchase) {
            return (
              <TableRow key={key} hover={hover} className={tableRowClasses}>
                <TableCell
                  className={classes.tableCell}
                  colSpan={prop.colspan}
                />
                <TableCell
                  className={classes.tableCell + " " + classes.right}
                  colSpan={prop.col.colspan}
                >
                  {prop.col.text}
                </TableCell>
              </TableRow>
            );
          }

          return (
            <TableRow
              key={key}
              hover={hover}
              className={classes.tableRow + " " + tableRowClasses}
            >
              {prop.map((cellValue, cellKey) => {
                const mapping = visibleColumnMapping.mapping[cellKey];
                const isCollapsedColumn =
                  mapping && mapping.type === "collapsed";

                const tableCellClasses =
                  classes.tableCell +
                  " " +
                  cx({
                    [classes[colorsColls[coloredColls.indexOf(cellKey)]]]:
                      coloredColls.indexOf(cellKey) !== -1,
                    [customCellClasses[customClassesForCells.indexOf(cellKey)]]:
                      customClassesForCells.indexOf(cellKey) !== -1,
                    [classes.collapsedCell]: isCollapsedColumn,
                  });

                return (
                  <TableCell className={tableCellClasses} key={cellKey}>
                    {typeof cellValue === "boolean"
                      ? cellValue.toString()
                      : cellValue}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

GroupedTable.defaultProps = {
  tableHeaderColor: "gray",
  hover: false,
  colorsColls: [],
  coloredColls: [],
  striped: false,
  customCellClasses: [],
  customClassesForCells: [],
  customHeadCellClasses: [],
  customHeadClassesForCells: [],
  columnGroups: null,
};

GroupedTable.propTypes = {
  tableHeaderColor: PropTypes.oneOf([
    "warning",
    "primary",
    "danger",
    "success",
    "info",
    "rose",
    "gray",
  ]),
  tableHead: PropTypes.arrayOf(PropTypes.string),
  tableData: PropTypes.array,
  columnGroups: PropTypes.shape({
    groups: PropTypes.object,
    column_to_group: PropTypes.object,
    group_order: PropTypes.array,
  }),
  hover: PropTypes.bool,
  coloredColls: PropTypes.arrayOf(PropTypes.number),
  colorsColls: PropTypes.array,
  customCellClasses: PropTypes.arrayOf(PropTypes.string),
  customClassesForCells: PropTypes.arrayOf(PropTypes.number),
  customHeadCellClasses: PropTypes.arrayOf(PropTypes.string),
  customHeadClassesForCells: PropTypes.arrayOf(PropTypes.number),
  striped: PropTypes.bool,
  tableShopping: PropTypes.bool,
};
